'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { lmsClient } from '@/lib/api/lmsClient';
import { Input, Button, AppLogo } from '@/components/ui';
import { Lock, User, ArrowRight, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { LMS_EVENTS } from '@/lib/analytics';

export default function LoginPage() {
  const { login } = useAuth();
  const { track } = useAnalytics();
  const router = useRouter();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const user = await lmsClient.auth.login(username, password);
      login(user);
      window.location.href = '/courses';
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Login failed. Check your credentials.';
      track(LMS_EVENTS.LOGIN_FAILED, { errorMessage: message });
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-center bg-slate-50 relative overflow-hidden px-4">
      {/* Ambient background glow blobs */}
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] sm:w-[500px] sm:h-[500px] rounded-full bg-brand-primary/5 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/2 translate-y-1/2 w-[350px] h-[350px] sm:w-[500px] sm:h-[500px] rounded-full bg-brand-secondary/5 blur-[120px] pointer-events-none" />

      {/* Main card wrapper */}
      <div className="w-full max-w-[440px] space-y-8 relative z-10 animate-fade-in-up">
        {/* Branding header block */}
        <div className="flex flex-col items-center text-center space-y-4">
          <div className="p-3 bg-white border border-brand-border rounded-2xl shadow-sm inline-flex transition-transform hover:scale-105 duration-300">
            <AppLogo size={42} />
          </div>
          <div className="space-y-1">
            <h1 className="text-2xl sm:text-3xl font-black text-brand-text-dark tracking-tight leading-tight">
              Spreadd Learning Platform
            </h1>
            <p className="text-sm text-brand-text-muted font-medium">
              Sign in to access your course dashboard.
            </p>
          </div>
        </div>

        {/* Login form Card */}
        <form
          onSubmit={handleSubmit}
          className="bg-white p-6 sm:p-8 rounded-3xl border border-brand-border shadow-2xl shadow-slate-100/50 space-y-5"
        >
          {error && (
            <div className="text-xs font-bold text-brand-error bg-brand-error/5 border border-brand-error/15 rounded-2xl px-4 py-3 flex items-start gap-2.5 animate-fade-in">
              <AlertTriangle size={18} className="shrink-0" />
              <span className="leading-relaxed">{error}</span>
            </div>
          )}

          <div className="space-y-4">
            {/* Username Input */}
            <div className="space-y-1.5">
              <label
                htmlFor="username"
                className="block text-xs font-semibold font-black text-brand-text-dark uppercase tracking-widest pl-1"
              >
                Username or Email
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-brand-text-muted group-focus-within:text-brand-primary transition-colors">
                  <User size={18} />
                </div>
                <Input
                  id="username"
                  type="text"
                  placeholder="Enter your username..."
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  className="pl-11 border-2 focus:ring-0 focus:outline-none"
                  error={!!error}
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center px-1">
                <label
                  htmlFor="password"
                  className="block text-xs font-semibold font-black text-brand-text-dark uppercase tracking-widest"
                >
                  Password
                </label>
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-brand-text-muted group-focus-within:text-brand-primary transition-colors">
                  <Lock size={18} />
                </div>
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password..."
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="pl-11 pr-11 border-2 focus:ring-0 focus:outline-none"
                  error={!!error}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-brand-text-muted hover:text-brand-text-dark transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
          </div>

          <div className="pt-3">
            <Button
              type="submit"
              loading={loading}
              fullWidth
              className="py-3.5 rounded-2xl bg-brand-primary hover:bg-brand-primary-dark shadow-lg shadow-brand-primary/10 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wider"
            >
              <span>{loading ? 'Signing in...' : 'Sign In'}</span>
              {!loading && <ArrowRight size={16} />}
            </Button>
          </div>
        </form>

        {/* Footer info */}
        {/* <div className="text-center text-[10px] text-brand-text-muted font-bold tracking-wide uppercase">
          © {new Date().getFullYear()} Spreadd Inc. All rights reserved.
        </div> */}
      </div>
    </div>
  );
}
